<template>
  <mf-loading-dialog :loading="isLoadingCampaign">
    <base-page-layout class="alteracao-gesta-ofertas campaign-container" :title="getPageTitle">
      <template v-if="['active', 'finished'].includes(campanha.status)" v-slot:title-complement>
        <div>
          <mf-button
            label="Ver resultados da oferta"
            outlined
            text-transform="none"
            icon="monitoring"
            icon-position="left"
            class="offer-results-button"
            @click="emitResultsFunction"
          />
        </div>
      </template>

      <div v-show="!confirmCampaign && !campaignActivated">
        <mf-info-card
          v-if="automaticCampaign"
          class="mb-3"
          variant="warning"
          description="Esta campanha foi importada automaticamente do seu ERP. As edições dessas ofertas serão sobrescritas na próxima carga de ofertas."
        />

        <mf-info-card
          v-if="disabledUnitsPermission"
          class="mb-3"
          variant="error"
          description="Não é possível editar esta campanha. Ela possui ao menos uma loja que você não tem permissão para acessar. Consulte seu usuário administrador para
            avaliar o permissionamento."
        />

        <mf-info-card
          v-if="hasFranchiseQueryParam"
          class="mb-3"
          variant="error"
          description="Essa é uma visualização das ofertas criadas na rede. Não é possível editar nenhum campo."
        />

        <header-trade
          v-if="isIndustryCampaign"
          ref="headerTrade"
          :campaign="campanha"
          :can-alter-trade-offers="canAlterTradeOffers"
          :is-disabled-in-rules="isDisabledInRules"
          :show-results-button="false"
          :campaign-date-errors="campaignDateErrors"
          :proposal-deadline-errors.sync="proposalDeadlineErrors"
          :cashback-expiration-date-errors.sync="cashbackExpirationDateErrors"
          @changeStartDate="validTradeOffersDate"
          @changeEndDate="validTradeOffersDate"
        />
        <div v-else>
          <title-description
            id="campaign-title"
            class="mb-2"
            use-card-title
            :disable-title="isTitleDisabled"
            :disable-description="isDescriptionDisabled"
            :title.sync="campanha.name"
            :description.sync="campanha.description"
            :status="campanha.status"
            :title-error="$v.campanha.name && $v.campanha.name.$error"
            :title-error-message="errorTitle"
            :description-error="$v.campanha.description && $v.campanha.description.$error"
            :description-error-message="errorDescription"
            :tooltip-message="tooltipIndustryMessage"
            :is-industry-campaign="isIndustryCampaign"
            :show-status-processing-message="includesThisStatus(['processing'])"
          >
            <template #additional-content>
              <div class="title-description-additional-content">
                <v-divider></v-divider>
                <div class="select-units-container">
                  <label class="mf-select-units-label">Lojas participantes</label>
                  <mf-select-units-dialog
                    id="campaign-units"
                    outlined
                    :disabled="includesThisStatus(['processing'])"
                    :disable-save="disableSave"
                    :selected-units.sync="campanha.units"
                    :selected-units-configuration.sync="campanha.units_configuration"
                    :unit-error="$v.campanha.units && $v.campanha.units.$error"
                    :units="units"
                    :tag-list="tagList"
                    :group-list="unitsGroups"
                    :default-data-type="defaultDataType"
                    dense
                    hide-ecommerce-configuration
                  />
                </div>
              </div>
            </template>
          </title-description>
        </div>

        <mf-card-container v-if="$getAllowedModules()['offers_auto_distributed']" title="Tipo de campanha" class="card-container my-6">
          <campaign-model-toggle :campaign="campanha" :validations="$v" />
        </mf-card-container>

        <mf-card-container class="my-6" title="Período de validade">
          <div class="mt-2 validity-period">
            <div class="input-container">
              <span>Data inicial</span>
              <date-picker
                :min="todayDate"
                :date.sync="campanha.start_date"
                :error-messages="errorStartDate"
                :disabled="isSelectDateDisabled || campanha.status === 'active'"
                outlined
                dense
                @input="handleSelectStartDate"
              />
            </div>

            <div v-if="hasFlashSales" class="input-container">
              <span>Horário</span>
              <mf-time-picker
                v-model="campanha.start_time"
                :disabled="isSelectDateDisabled || campanha.status === 'active'"
                dense
                outlined
                show-gmt-message
                :error-messages="startTimeErrorMessage"
                @input="handleSelectStartTime"
              />
            </div>

            <div class="separator"></div>

            <div class="input-container">
              <span>Data final</span>
              <date-picker
                :min="todayDate"
                :date.sync="campanha.end_date"
                :error-messages="errorEndDate"
                :disabled="isSelectDateDisabled"
                outlined
                dense
                @input="handleSelectEndDate"
              />
            </div>

            <div v-if="hasFlashSales" class="input-container">
              <span>Horário</span>
              <mf-time-picker
                v-model="campanha.end_time"
                show-gmt-message
                :disabled="isSelectDateDisabled"
                dense
                outlined
                :min="calculatedMinEndTime"
                :error-messages="endTimeErrorMessage"
              />
            </div>

            <div v-if="hasFlashSales" class="separator"></div>

            <div v-if="hasFlashSales" class="input-container margin-plus">
              <span>Margem adicional compra</span>
              <mf-tooltip max-width="250" bottom variant="secondary">
                <template #activator="{ on }">
                  <mf-number-input
                    v-model="campanha.pdv_margin_time"
                    class="margin-plus-input"
                    outlined
                    dense
                    prepend-inner-icon="mdi-clock-outline"
                    :disabled="isSelectDateDisabled"
                    :error-messages="pdvMarginTimeErrorMessage"
                    :value="campanha.pdv_margin_time"
                    :min="0"
                    :max="60"
                    :maxlength="2"
                    suffix="minutos"
                    :show-arrows="false"
                    v-on="on"
                  />
                </template>
                <span>
                  Para garantir que os consumidores possam finalizar suas compras em andamento mesmo após o término da oferta, um tempo adicional é inserido na
                  hora final da oferta no PDV. Esta margem de segurança, medida em minutos, é aplicada exclusivamente no sistema do PDV e permanece invisível
                  para os consumidores no Aplicativo.
                </span>
              </mf-tooltip>
            </div>
          </div>

          <div v-if="hasFlashSales" class="flash-sale-container">
            <div>
              <mf-toggle v-model="campanha.flash" label="Oferta relâmpago" class="ma-0" hide-details dense />
              <p>Ative a Oferta relâmpago para ofertas com o prazo curto, que criam um senso de urgência nos consumidores.</p>
            </div>
            <div>
              <span>Como ficará no app:</span>
              <img :src="require('@/assets/flash_sale_preview.svg')" alt="Banner da campanha relâmpago" />
            </div>
          </div>
        </mf-card-container>

        <v-container v-if="campanha.industry" class="pa-0 mb-2" fluid>
          <v-row no-gutters>
            <v-col cols="6">
              <terms-accept-card
                :campaign="campanha"
                :disabled="isChangingAcceptanceDisabled"
                :terms-accepted="termsConditionsAccepted"
                @acceptance-change="updateAcceptance"
              />
            </v-col>
            <v-col cols="6">
              <accept-history-card :campaign="campanha" />
            </v-col>
          </v-row>
        </v-container>

        <v-container class="px-0 py-0 campaign-target-and-model" fluid>
          <mf-card-container :title="$t('campanhas.publico_alvo')" class="card-container">
            <publico-alvo
              :single-select="true"
              :segmentos.sync="campanha.segments"
              :is-campaign.sync="isCampaign"
              :disable="isSegmentsDisabled"
              :block-segments-update="includesThisStatus(['finished', 'active', 'expired', 'industry_rejected', 'processing'])"
              :is-static-segment="isStaticSegment"
              :is-industry-campaign="isIndustryCampaign"
              :tooltip-industry-message="tooltipIndustryMessage"
              :campaign-id="campanha._id"
              :disable-segment-view="!campanha.has_segment_data || !campanha.status || includesThisStatus(['draft'])"
              :disable-segment-view-message="disableSegmentViewMessage"
              :campaign-created-at="campanha.created_at"
            />
            <mf-info-card v-if="showSegmentsWarningRow" variant="info" :v-html="frozenSegmentWarningInfoCardHtml" class="mt-5" />
            <mf-info-card v-else variant="info" :v-html="noSegmentWarningInfoCardHtml" class="mt-5" />
          </mf-card-container>

          <mf-card-container title="Modelo da campanha" class="card-container">
            <tooltip fit-content align="left" :disabled="isTooltipActivableCampaignDisabled" :message="tooltipIndustryMessage">
              <v-checkbox
                v-model="campanha.activatable"
                color="primary"
                label="Campanha ativável"
                :disabled="isActivableCampaignDisabled"
                data-cy="check-activatable"
              />
            </tooltip>
            <mf-info-card v-if="showSegmentsWarningRow" variant="info" :v-html="activatableOfferWarningInfoCardHtml" class="mt-auto" />
          </mf-card-container>
        </v-container>

        <offer-sales-table-headers
          v-if="!isLoadingCampaign"
          :search.sync="search"
          :campaign="cloneDeep(campanha)"
          :offers-type-tab-high-level.sync="offersTypeTabHighLevel"
          :offers-type-tabs-high-level="offersTypeTabsHighLevel"
          :offers-type-tab-low-level.sync="offersTypeTabLowLevel"
          :offers-type-tabs-low-level="offersTypeTabsLowLevel"
          :offers-type-tabs-with-errors-low-level="offersTypeTabsWithErrorsLowLevel"
          :is-disabled-in-rules="isDisabledInRules"
          :is-industry-campaign="isIndustryCampaign"
          :tooltip-message="tooltipIndustryMessage"
          :automatic-campaign="automaticCampaign"
          :cashback-expiration-type.sync="cashbackExpiration.type"
          :expiration-in-days.sync="cashbackExpiration.expiration_in_days"
          :expire-date.sync="cashbackExpiration.expires_at"
          :cashback-expiration-error-message="cashbackExpirationErrorMessage"
          :hide-buttons="hasFranchiseQueryParam"
          @insert-products="insertProducts"
        />
        <div v-for="(offerTable, index) in offerTables" :key="index">
          <component
            :is="offerTable.component"
            v-if="offerTable.isVisible"
            v-show="offerTable.show"
            :ref="offerTable.ref"
            :offer-sales-type="offerTable.offerSalesType"
            :campaign="campanha"
            :all-inputs-required="allInputsRequired"
            :repeated-skus="allRepeatedProducts"
            :is-disabled-in-rules="isDisabledInRules"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipIndustryMessage"
            :search="search"
            :disabled="includesThisStatus(['processing']) || hasFranchiseQueryParam"
          />
        </div>
        <products-on-offer-combo
          v-show="offersTypeTabHighLevel === 0 && offersTypeTabLowLevel === (isOffersByUnitPermission ? 4 : 3)"
          ref="productsCombo"
          :campaign="campanha"
          :offer-sales-key-name="offerSalesKeyNames.combo_offer_sales"
          :second-offer-sales-key-name="offerSalesKeyNames.target_products"
          :products.sync="campanha.combo_offer_sales"
          :eans.sync="eans"
          :automatic-campaign="automaticCampaign"
          :segments="campanha.segments"
          :status-campaign="campanha.status"
          :repeated-skus="allRepeatedProducts"
          :is-disabled-in-rules="isDisabledInRules"
          :is-industry-campaign="isIndustryCampaign"
          :tooltip-message="tooltipIndustryMessage"
          :disabled="includesThisStatus(['processing']) || hasFranchiseQueryParam"
          @update-eans="updateEans"
        />

        <mf-action-buttons
          class="mt-2"
          :primary-button="primaryButton"
          :secondary-button="secondaryButton"
          :extra-button="deleteButton"
          :cancel-button="cancelButton"
        />
      </div>

      <div v-if="confirmCampaign">
        <confirm-offer-campaign
          ref="confirm"
          :timer-total-value="timerTotalValue"
          :campanha="campanha"
          :confirm-campaign.sync="confirmCampaign"
          :offer-sales-key-names="offerSalesKeyNames"
          :eans="eans"
          :is-loading="loading"
          :is-new-offer="isNewOffer"
          :new-offer-schedule-id-to-compare="newOfferScheduleIdToCompare"
          :all-single-products="allSingleProducts"
          :cashback-expiration="cashbackExpiration"
          :unavailable-products="unavailableProducts"
          @subscription-finished="finishSubscription(newOfferScheduleIdToCompare)"
          @subscription-error="handleSubscriptionError"
          @save="checkForIndustryBeforeSave"
          @save-draft="saveDraft"
          @back-to-offers-list="backToOffersList"
          @back-to-offers-list-skip="backToOffersList(true)"
        />
      </div>

      <communicate-by-channel
        v-if="campaignActivated"
        :error="showErrorDialog"
        :is-new-offer="isNewOffer"
        :offer-schedule-id="offerScheduleId"
        :segments="campanha.segments"
        :is-dynamic-pricing-percentage="isDynamicPricingPercentage"
        :is-intelligent-distribution="isIntelligentDistributionEnabled"
        :is-flash="isFlash"
        @returnToIndex="backToOffersList"
      />
      <offer-schedule-error-dialog v-model="showErrorDialog" :is-new-offer="isNewOffer" />
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import findIndex from 'lodash/findIndex'
import Swal from 'sweetalert2'
import { mapActions, mapGetters } from 'vuex'
import { required, minLength, minValue } from 'vuelidate/lib/validators'
import { assertArray } from '@/config/helpers.js'
import { DateTime } from 'luxon'
import BasePageLayout from '@/components/BasePageLayout.vue'
import TitleDescription from '@/modules/campanhas/views/components/edit/TitleDescription.vue'
import { notOnlySpace, isFuture } from '@/helpers/validators.js'
import PublicoAlvo from '@/modules/campanhas/views/components/PublicoAlvo.vue'
import ConfirmOfferCampaign from '@/modules/campanhas/gestao-ofertas-v2/views/components/ConfirmOfferCampaign.vue'
import WarningRow from '@/components/WarningRow.vue'
import OfferSalesTableHeaders from '../components/OfferSalesTableHeaders.vue'
import {
  QUERY_SALES_CAMPAIGN_BY_ID,
  MUTATION_UPSERT_SALES_CAMPAIGN,
  MUTATION_UPSERT_DRAFT_SALES_CAMPAIGN,
  MUTATION_FINISH_SALES_CAMPAIGN,
  MUTATION_UNSCHEDULED_SALES_CAMPAIGN,
  MUTATION_DELETE_SALES_CAMPAIGN,
  MUTATION_SAVE_CRITERIA_ON_OFFER_SCHEDULE,
} from '../../graphql'
import { QUERY_GET_PRODUCTS_AND_GROUPS } from '@/modules/produtos/graphql'

import { MUTATION_STATICALLY_CLONE_SEGMENTS } from '@/modules/campanhas/views/components/graphql'
import { getAllSingleProductsOutsideAndInsideGroup } from '@/modules/campanhas/gestao-ofertas-v2/helpers/products.js'
import { rules } from '../../helpers/rules.js'
import Tooltip from '../components/Tooltip.vue'
import { industryWarnings } from '../mixins/industry-warnings'
import CampaignModelToggle from '../components/CampaignModelToggle.vue'
import intelligentDistributionMixin from '../mixins/intelligent-distribution.js'

import AbsoluteDiscount from '../components/new-offer-sales-tables/absolute-discount/AbsoluteDiscount.vue'
import PercentageDiscount from '../components/new-offer-sales-tables/percentage-discount/PercentageDiscount.vue'
import DynamicPricingPercentageDiscount from '../components/new-offer-sales-tables/dynamic-pricing-percentage-discount/DynamicPricingPercentageDiscount.vue'
import TakePay from '../components/new-offer-sales-tables/take-pay/TakePay.vue'
import CashbackAndPoints from '../components/new-offer-sales-tables/cashback-points/CashbackAndPoints.vue'
import ProductsOnOfferCombo from '@/modules/campanhas/gestao-ofertas-v2/views/components/offer-combo/ProductsOnOfferCombo.vue'

import OfferScheduleErrorDialog from '../components/OfferScheduleErrorDialog.vue'
import AcceptHistoryCard from '@/modules/campanhas/gestao-ofertas-v2/views/components/AcceptHistoryCard.vue'
import TermsAcceptCard from '@/modules/campanhas/gestao-ofertas-v2/views/components/TermsAcceptCard.vue'
import HeaderTrade from '@/modules/industria/ofertas-trade/views/components/HeaderTrade.vue'
import DatePicker from '@/components/DatePicker.vue'
import { tradeCampaignAlterations } from '@/modules/industria/ofertas-trade/views/mixins/trade-campaign-alterations'
import { campaignHelpers } from '../mixins/campaign-helpers'

export default {
  name: 'Edit',
  components: {
    BasePageLayout,
    TitleDescription,
    PublicoAlvo,
    ConfirmOfferCampaign,
    WarningRow,
    Tooltip,
    OfferSalesTableHeaders,
    CommunicateByChannel: () => import('@/modules/campanhas/gestao-ofertas-v2/views/components/CommunicateByChannel.vue'),

    ProductsOnOfferCombo,
    AbsoluteDiscount,
    PercentageDiscount,
    DynamicPricingPercentageDiscount,
    TakePay,
    CashbackAndPoints,
    OfferScheduleErrorDialog,
    AcceptHistoryCard,
    TermsAcceptCard,
    HeaderTrade,
    DatePicker,
    CampaignModelToggle,
  },

  mixins: [industryWarnings, tradeCampaignAlterations, campaignHelpers, intelligentDistributionMixin],

  data: () => ({
    timerTotalValue: 0,
    showErrorDialog: false,
    loading: false,
    offersTypeTabHighLevel: 0,
    offersTypeTabLowLevel: 0,
    queryItems: [],
    date: [],
    search: '',
    isCampaign: true,
    cashbackExpiration: {
      type: 'AT_PURCHASE',
      expiration_in_days: 1,
      expires_at: '',
    },
    campanha: {
      name: '',
      description: '',
      eans: [],
      units: [],
      units_configuration: [],
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
      pdv_margin_time: 30,
      offer_sales: [],
      percentage_offer_sales: [],
      dynamic_pricing_percentage_offer_sales: [],
      cashback_percentage_offer_sales: [],
      cashback_absolute_offer_sales: [],
      next_buy_cashback_offer_sales: [],
      dynamic_rule_offer_sales: [],
      cross_sell_offer_sales: [],
      points_offer_sales: [],
      takepay_offer_sales: [],
      combo_offer_sales: [],
      active: false,
      segments: [],
      activatable: false,
      industry: null,
      rules: [],
      has_segment_data: false,
      partner_units: [],
      auto_distributed: false,
      user_distribution_models: [],
    },
    segmentsIds: [],
    offerSalesKeyNames: {
      offer_sales: 'offer_sales',
      percentage_offer_sales: 'percentage_offer_sales',
      dynamic_pricing_percentage_offer_sales: 'dynamic_pricing_percentage_offer_sales',
      takepay_offer_sales: 'takepay_offer_sales',
      combo_offer_sales: 'combo_offer_sales',
      target_products: 'target_products',
      cashback_percentage_offer_sales: 'cashback_percentage_offer_sales',
      cashback_absolute_offer_sales: 'cashback_absolute_offer_sales',
      points_offer_sales: 'points_offer_sales',
      next_buy_cashback_offer_sales: 'next_buy_cashback_offer_sales',
    },
    eans: {
      offer_sales: [],
      percentage_offer_sales: [],
      dynamic_pricing_percentage_offer_sales: [],
      cashback_percentage_offer_sales: [],
      next_buy_cashback_offer_sales: [],
      takepay_offer_sales: [],
      trigger_product: [],
      target_products: [],
      combo_offer_sales: [],
    },
    productEans: [],
    eansError: [],
    disableButtons: false,
    disabledUnitsPermission: false,
    unitError: Boolean,
    initDateError: Boolean,
    endDateError: Boolean,
    allInputsRequired: false,
    showOfertasPersonalizadas: false,
    automaticCampaign: false,
    confirmCampaign: false,
    tryingToSaveAsDraft: false,
    allOfferSalesValidations: [],
    campaignActivated: false,
    offerScheduleId: '',
    defaultDataType: null,
    termsConditionsAccepted: false,
    tooltipIndustryMessage: 'Desabilitado devido a pré-configuração da indústria',
    rules,
    DateTime: DateTime,
    isStaticSegment: false,
    loadingCampaign: true,
    newOfferScheduleIdToCompare: null,
    skipRouteValidaton: false,
    unavailableProducts: [],
  }),
  computed: {
    ...mapGetters({ quickAction: 'getQuickAction' }),
    ...mapGetters(['units', 'tagList', 'unitsGroups']),
    isNewOffer() {
      return !this.$route.params.id
    },
    isJaneModulePermission() {
      return this.$getAllowedModules()['jane']
    },
    isOffersByUnitPermission() {
      return this.$getAllowedModules()['offers_by_unit']
    },
    hasFlashSales() {
      if (this.isIntelligentDistributionEnabled) {
        return false
      }
      return this.$getAllowedModules()['offers_with_time'] && this.isJaneModulePermission && this.$getAllowedModules()['mobile']
    },
    isLoadingCampaign() {
      if (this.isNewOffer) return false
      return this.loadingCampaign
    },
    isWaitingTimer() {
      return typeof this.timerTotalValue === 'number' && this.timerTotalValue > 0
    },
    getConfirmMessage() {
      if (this.isWaitingTimer) {
        return {
          title: 'Tem certeza?',
          message: 'Saindo dessa página, antes da finalizarmos o processamento da sua campanha, você não receberá a notificação de confirmação.',
          confirmText: 'SAIR',
          cancelText: 'FICAR NESSA PÁGINA',
        }
      }
      return {
        title: 'Tem certeza?',
        message:
          'Sua campanha não está salva. Para ativá-la confirme as informações apresentadas. Para editar ou criar um rascunho clique em VOLTAR no canto inferior direito da sua tela.',
        cancelText: 'Ficar e corrigir',
        confirmText: 'Descartar alterações',
      }
    },
    frozenSegmentWarningInfoCardHtml() {
      if (this.isIntelligentDistributionEnabled) {
        return '<span style="color: #004D99">Nas campanhas automáticas, é obrigatório selecionar um segmento. A campanha valerá apenas para os clientes que estiverem no segmento no momento da ativação; quem entrar no segmento depois não receberá as ofertas.</span>'
      }
      return '<span style="color: #004D99">Ao selecionar segmentos, a campanha será de ofertas personalizadas, e passa a valer <b> apenas </b> para os clientes contidos nos segmentos no momento de ativação da campanha. Clientes que entrarem no segmento após a ativação da campanha não irão participar da oferta.</span>'
    },
    noSegmentWarningInfoCardHtml() {
      return '<span style="color: #004D99">Nenhum segmento para essa oferta.</span>'
    },
    activatableOfferWarningInfoCardHtml() {
      return '<span style="color: #004D99">Ativando essa opção o consumidor deverá ativar essas ofertas pelo aplicativo, antes de poder aproveitá-las.</span>'
    },
    errorStartDate() {
      if (!this.$v.campanha || !this.$v.campanha.start_date) return ''
      if (this.$v.campanha.start_date.$error) {
        if (!this.$v.campanha.start_date.required) return 'Selecione uma data de início.'
        else if (this.campanha.status !== 'active' && !this.$v.campanha.start_date.isFuture) return 'A data de validade deve ser no futuro.'
      }
      return ''
    },
    errorEndDate() {
      if (!this.$v.campanha || !this.$v.campanha.end_date) return ''
      if (this.$v.campanha.end_date.$error) {
        if (!this.$v.campanha.end_date.required) return 'Selecione uma data de fim.'
        else if (this.campanha.status !== 'active' && !this.$v.campanha.end_date.isFuture) return 'A data de validade deve ser no futuro.'
        else if (!this.$v.campanha.end_date.endDateBiggerThanStart) return 'A data de fim deve ser maior que a de início.'
      }
      return ''
    },
    todayDate() {
      return DateTime.now().toFormat('yyyy-MM-dd')
    },
    calculatedMinEndTime() {
      if (this.campanha.start_date === this.campanha.end_date) {
        if (this.campanha.start_time >= '22:00') return '24:00'
        return DateTime.fromFormat(this.campanha.start_time, 'HH:mm').plus({ hours: 2 }).toFormat('HH:mm')
      }
      return ''
    },
    errorTitle() {
      if (!this.$v.campanha.name) return ''
      if (this.$v.campanha.name.$error) {
        if (!this.$v.campanha.name.required) return 'Dê um nome para esta campanha.'
        else if (!this.$v.campanha.name.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.campanha.name.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    errorDescription() {
      if (!this.$v.campanha.description) return ''
      if (this.$v.campanha.description.$error) {
        if (!this.$v.campanha.description.required) return 'Informe detalhes para melhor identificação da campanha.'
        else if (!this.$v.campanha.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        else if (!this.$v.campanha.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    getPageTitle() {
      if (this.confirmCampaign) return 'Confirme sua campanha'
      if (this.isNewOffer) return 'Nova campanha'
      if (this.includesThisStatus(['finished', 'expired', 'industry_rejected'])) return 'Visualizar campanha'
      return 'Editar campanha'
    },
    allOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campanha?.offer_sales)
    },
    allPercentageOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campanha?.percentage_offer_sales)
    },
    allDynamicPricingPercentageOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campanha?.dynamic_pricing_percentage_offer_sales)
    },
    allCashbackPercentageOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campanha?.cashback_percentage_offer_sales)
    },
    allCashbackAbsoluteOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campanha?.cashback_absolute_offer_sales)
    },
    allPointsOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campanha?.points_offer_sales)
    },
    allNextbuyCashbackOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campanha?.next_buy_cashback_offer_sales)
    },
    allTakeAndPayOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campanha?.takepay_offer_sales)
    },
    allComboOfferSalesProducts() {
      if (this.campanha.combo_offer_sales) {
        const productsArray = this.campanha.combo_offer_sales.reduce((accumulator, current) => {
          accumulator.push(...current.trigger_products)
          accumulator.push(...current.target_products)
          return accumulator
        }, [])

        return getAllSingleProductsOutsideAndInsideGroup(productsArray)
      }

      return []
    },
    isAdvancedOffer() {
      return this.$getAllowedModules()['advanced_discounts']
    },
    hasCashbackModule() {
      return this.$getAllowedModules()['cashback']
    },
    isPoints() {
      return this.$getAllowedModules()['points']
    },
    isNextbuyCashback() {
      return this.campanha?.next_buy_cashback_offer_sales?.length > 0
    },
    isDynamicPricingPercentage() {
      return this.campanha?.dynamic_pricing_percentage_offer_sales?.length > 0
    },
    isFlash() {
      return this.campanha?.flash
    },
    offersTypeTabsHighLevel() {
      let offersTabs = [
        {
          name: 'Descontos e ofertas',
          haveLength: false,
          icon: 'sell',
        },
      ]

      const showCashbackTab = (this.hasCashbackModule || this.isPoints || this.isIndustryCampaign) && !this.isIntelligentDistributionEnabled
      if (showCashbackTab) {
        const cashbackTab = {
          name: 'Cashback e pontos',
          haveLength: false,
          icon: 'currency_exchange',
        }
        offersTabs = offersTabs.concat(cashbackTab)
      }
      return offersTabs
    },
    offersTypeTabsLowLevel() {
      let offersTabs = []

      const absoluteDiscount = [
        {
          name: 'Desconto absoluto',
          itemsLength: getAllSingleProductsOutsideAndInsideGroup(this.campanha.offer_sales)?.length || 0,
          haveLength: true,
          offerSalesKeyName: this.offerSalesKeyNames.offer_sales,
          highLevel: 0,
          tabIndex: 0,
        },
      ]
      offersTabs = offersTabs.concat(absoluteDiscount)
      if (this.isAdvancedOffer) {
        const advancedOffers = [
          {
            name: 'Desconto percentual',
            itemsLength: this.allPercentageOfferSalesProducts?.length || 0,
            haveLength: true,
            offerSalesKeyName: this.offerSalesKeyNames.percentage_offer_sales,
            highLevel: 0,
            tabIndex: 1,
          },
          ...(this.isOffersByUnitPermission
            ? [
                {
                  name: 'Desconto percentual por loja',
                  itemsLength: this.allDynamicPricingPercentageOfferSalesProducts?.length || 0,
                  haveLength: true,
                  offerSalesKeyName: this.offerSalesKeyNames.dynamic_pricing_percentage_offer_sales,
                  highLevel: 0,
                  tabIndex: 2,
                },
              ]
            : []),
          {
            name: 'Leve e pague',
            itemsLength: this.allTakeAndPayOfferSalesProducts?.length || 0,
            haveLength: true,
            offerSalesKeyName: this.offerSalesKeyNames.takepay_offer_sales,
            highLevel: 0,
            tabIndex: this.isOffersByUnitPermission ? 3 : 2,
          },
          {
            name: 'Combo',
            itemsLength: this.allComboOfferSalesProducts?.length || 0,
            haveLength: true,
            offerSalesKeyName: this.offerSalesKeyNames.combo_offer_sales,
            highLevel: 0,
            tabIndex: this.isOffersByUnitPermission ? 4 : 3,
          },
        ]

        offersTabs = offersTabs.concat(advancedOffers)
      }

      if (this.hasCashbackModule) {
        const cashbackPercentageTab = {
          name: 'Cashback percentual',
          itemsLength: this.allCashbackPercentageOfferSalesProducts?.length || 0,
          haveLength: true,
          offerSalesKeyName: this.offerSalesKeyNames.cashback_percentage_offer_sales,
          highLevel: 1,
          tabIndex: this.isOffersByUnitPermission ? 5 : 4,
        }
        offersTabs = offersTabs.concat(cashbackPercentageTab)

        const cashbackAbsoluteTab = {
          name: 'Cashback absoluto',
          itemsLength: this.allCashbackAbsoluteOfferSalesProducts?.length || 0,
          haveLength: true,
          offerSalesKeyName: this.offerSalesKeyNames.cashback_absolute_offer_sales,
          highLevel: 1,
          tabIndex: this.isOffersByUnitPermission ? 6 : 5,
        }
        offersTabs = offersTabs.concat(cashbackAbsoluteTab)
      }

      if (this.isPoints) {
        const pointsTab = {
          name: 'Pontos',
          itemsLength: this.allPointsOfferSalesProducts?.length || 0,
          haveLength: true,
          offerSalesKeyName: this.offerSalesKeyNames.points_offer_sales,
          highLevel: 1,
          tabIndex: this.isOffersByUnitPermission ? 7 : 6,
        }
        offersTabs = offersTabs.concat(pointsTab)
      }

      if (this.isNextbuyCashback) {
        const nextbuyCashbackTab = {
          name: 'Cashback',
          itemsLength: this.allNextbuyCashbackOfferSalesProducts?.length || 0,
          haveLength: true,
          offerSalesKeyName: this.offerSalesKeyNames.next_buy_cashback_offer_sales,
          highLevel: 1,
          tabIndex: this.isOffersByUnitPermission ? 8 : 7,
        }
        offersTabs = offersTabs.concat(nextbuyCashbackTab)
      }

      return offersTabs
    },
    allSingleProducts() {
      const getOfferSalesWithKeyName = (offerSales, keyName) =>
        assertArray(offerSales).map(({ sku, franchisee_eans_frequency_percentage, name, image_url, sync_data }) => ({
          sku,
          name,
          image_url,
          offerSalesKeyName: keyName,
          franchisee_eans_frequency_percentage,
          sync_data,
        }))
      const offerSalesWithKeyName = getOfferSalesWithKeyName(this.allOfferSalesProducts, this.offerSalesKeyNames.offer_sales)
      const percentageOfferSalesWithKeyName = getOfferSalesWithKeyName(this.allPercentageOfferSalesProducts, this.offerSalesKeyNames.percentage_offer_sales)
      const dynamicPricingPercentageOfferSalesWithKeyName = getOfferSalesWithKeyName(
        this.allDynamicPricingPercentageOfferSalesProducts,
        this.offerSalesKeyNames.dynamic_pricing_percentage_offer_sales
      )
      const cashbackPercentageOfferSalesWithKeyName = getOfferSalesWithKeyName(
        this.allCashbackPercentageOfferSalesProducts,
        this.offerSalesKeyNames.cashback_percentage_offer_sales
      )
      const cashbackAbsoluteOfferSalesWithKeyName = getOfferSalesWithKeyName(
        this.allCashbackAbsoluteOfferSalesProducts,
        this.offerSalesKeyNames.cashback_absolute_offer_sales
      )
      const pointsOfferSalesWithKeyName = getOfferSalesWithKeyName(this.allPointsOfferSalesProducts, this.offerSalesKeyNames.points_offer_sales)
      const nextbuyCashbackOfferSalesWithKeyName = getOfferSalesWithKeyName(
        this.allNextbuyCashbackOfferSalesProducts,
        this.offerSalesKeyNames.next_buy_cashback_offer_sales
      )
      const takeAndPayOfferSalesWithKeyName = getOfferSalesWithKeyName(this.allTakeAndPayOfferSalesProducts, this.offerSalesKeyNames.takepay_offer_sales)
      const offerComboSalesWithKeyName = getOfferSalesWithKeyName(this.allComboOfferSalesProducts, this.offerSalesKeyNames.combo_offer_sales)
      const productsByOffersType = [
        offerSalesWithKeyName,
        percentageOfferSalesWithKeyName,
        dynamicPricingPercentageOfferSalesWithKeyName,
        takeAndPayOfferSalesWithKeyName,
        offerComboSalesWithKeyName,
        cashbackPercentageOfferSalesWithKeyName,
        cashbackAbsoluteOfferSalesWithKeyName,
        pointsOfferSalesWithKeyName,
        nextbuyCashbackOfferSalesWithKeyName,
      ]

      const allSingleProducts = productsByOffersType.reduce((allSingleProducts, productsByOfferType) => {
        if (productsByOfferType.length) allSingleProducts.push(...productsByOfferType)
        return allSingleProducts
      }, [])

      return allSingleProducts
    },
    allRepeatedProducts() {
      const allSingleProducts = this.isJaneModulePermission
        ? this.allSingleProducts.filter((product) => product.offerSalesKeyName !== 'combo_offer_sales')
        : this.allSingleProducts

      const products = allSingleProducts.reduce((allRepeatedProducts, product) => {
        const currentProductExistInRepeatedList = allRepeatedProducts.some((repeatedProduct) => repeatedProduct.sku === product.sku)

        if (!allRepeatedProducts.length || !currentProductExistInRepeatedList) {
          allRepeatedProducts.push({ ...product, quantity: 1 })
        } else {
          const productIndex = allRepeatedProducts.findIndex((repeatedProduct) => repeatedProduct.sku === product.sku)
          allRepeatedProducts[productIndex].quantity++
        }

        return allRepeatedProducts
      }, [])

      return products.filter((product) => product.quantity > 1).map(({ sku }) => sku)
    },
    offersTypeTabsWithErrorsLowLevel() {
      const offerSalesKeyNameWithErrors = new Set()
      let offerTypeTabWithErrors = []

      this.allRepeatedProducts.forEach((sku) => {
        this.allSingleProducts.forEach((product) => {
          if (product.sku === sku) {
            offerSalesKeyNameWithErrors.add(product.offerSalesKeyName)
          }
        })
      })

      this.offersTypeTabsLowLevel.forEach((tab) => {
        const existOfferSalesWithErrors = offerSalesKeyNameWithErrors.has(tab.offerSalesKeyName)

        if (!existOfferSalesWithErrors) offerTypeTabWithErrors.splice(tab.tabIndex, 1)
        else offerTypeTabWithErrors.push(tab.tabIndex)
      })

      this.allOfferSalesValidations.forEach((validation) => {
        if (!offerSalesKeyNameWithErrors.has(validation.name)) {
          offerTypeTabWithErrors.push(validation.index)
        }
      })

      if (this.$v?.cashbackExpiration?.$error) {
        if (this.campanha.cashback_percentage_offer_sales.length > 0) {
          offerTypeTabWithErrors.push(this.isOffersByUnitPermission ? 5 : 4)
        } else if (this.campanha.cashback_absolute_offer_sales.length > 0) {
          offerTypeTabWithErrors.push(this.isOffersByUnitPermission ? 5 : 6)
        }
      }
      return offerTypeTabWithErrors
    },
    cashbackExpirationErrorMessage() {
      if (this.$v?.cashbackExpiration?.$error) {
        if (!this.$v.cashbackExpiration?.type.required) return 'Informe um tipo de expiração de cashback.'
        if (this.cashbackExpiration.type === 'AT_PURCHASE') {
          if (!this.$v.cashbackExpiration?.expiration_in_days?.required) return 'Informe a data variável.'
          if (!this.$v.cashbackExpiration?.expiration_in_days?.minValue) return 'A data variável deve ser de no mínimo 1 dia.'
        } else {
          if (!this.$v.cashbackExpiration?.expires_at?.required) return 'Informe a data fixa.'
          if (!this.$v.cashbackExpiration?.expires_at?.endDateValidation) return 'A data deve ser maior que a data final da campanha.'
        }
        return ''
      } else return ''
    },
    startTimeErrorMessage() {
      if (this.$v?.campanha.start_time?.$error) {
        if (!this.$v.campanha.start_time.required) return 'Selecione um horário de início.'
      }
      return ''
    },
    endTimeErrorMessage() {
      if (this.$v?.campanha.end_time?.$error) {
        if (!this.$v.campanha.end_time.required) return 'Selecione um horário de fim.'
      }
      return ''
    },
    pdvMarginTimeErrorMessage() {
      if (this.$v?.campanha.pdv_margin_time?.$error) {
        if (!this.$v.campanha.pdv_margin_time.validateMargin) return 'A margem deve estar entre 0 e 60 minutos.'
      }
      return ''
    },
    endDateTimeErrorMessage() {
      if (this.$v?.campanha.end_time?.$error) {
        if (!this.$v.campanha.end_time.required) return 'Selecione um horário de fim.'
        if (!this.$v.campanha.end_time.validEndTime) return 'O horário de início deve ser no futuro.'
      }
      return ''
    },
    primaryButton() {
      const button = {
        isVisible: true,
        isDisabled: this.isDisabled,
        isLoading: this.loading,
        action: this.saveActive,
        tooltip: this.hasFranchiseQueryParam,
        tooltipMessage: this.hasFranchiseQueryParam ? 'Indisponível nessa campanha.' : '',
      }

      switch (this.campanha.status) {
        case 'active':
        case 'scheduled':
          button.text = 'Salvar alterações'
          break
        case 'industry_proposal':
          button.text = 'Aceitar campanha'
          break
        case 'draft':
        default:
          button.text = 'Ativar campanha'
      }
      return button
    },

    secondaryButton() {
      const button = {
        isVisible: true,
        isDisabled: this.isDisabled || this.automaticCampaign,
        isLoading: this.loading,
      }

      switch (this.campanha.status) {
        case 'active':
          button.text = 'Finalizar'
          button.action = this.confirmBeforeFinish
          break
        case 'scheduled':
          button.text = 'Suspender'
          button.action = this.confirmBeforeDisable
          break
        case 'draft':
        case 'industry_proposal':
          button.text = 'Salvar alterações'
          button.action = this.saveDraft
          break
        default:
          button.text = 'Salvar rascunho'
          button.action = this.saveDraft
      }
      return button
    },

    cancelButton() {
      const button = {
        action: this.backToOffersList,
        isVisible: true,
        isDisabled: false,
      }

      switch (this.campanha.status) {
        case 'active':
        case 'finished':
        case 'expired':
        case 'industry_rejected':
          button.text = 'Fechar'
          break
        default:
          button.text = 'Cancelar'
      }

      return button
    },

    deleteButton() {
      return {
        text: 'Apagar rascunho',
        action: this.deleteCampaign,
        isVisible: this.campanha.status === 'draft',
        isDisabled: this.disableButtons || this.automaticCampaign || this.isIndustryCampaign,
        isLoading: this.loading,
      }
    },

    isChangingAcceptanceDisabled() {
      return !this.includesThisStatus(['industry_proposal'])
    },

    isTooltipDateDisabled() {
      return this.campanha.industry ? !this.isSelectDateDisabled : true
    },

    isSelectDateDisabled() {
      return (
        this.includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']) ||
        this.automaticCampaign ||
        this.isDisabledInRules(this.rules.startDate) ||
        this.isDisabledInRules(this.rules.endDate) ||
        this.hasFranchiseQueryParam
      )
    },

    isSegmentsDisabled() {
      return (
        this.automaticCampaign ||
        this.includesThisStatus(['finished', 'active', 'scheduled', 'expired', 'industry_rejected', 'processing']) ||
        this.isDisabledInRules(this.rules.segments)
      )
    },

    isTooltipActivableCampaignDisabled() {
      return this.campanha.industry ? !this.isActivableCampaignDisabled : true
    },

    isActivableCampaignDisabled() {
      return this.campanha.industry
        ? true
        : this.includesThisStatus(['finished', 'active', 'scheduled', 'expired', 'industry_rejected', 'processing']) ||
            this.isDisabledInRules(this.rules.activatable)
    },

    isTitleDisabled() {
      return (
        this.automaticCampaign ||
        this.includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']) ||
        this.isDisabledInRules(rules.name) ||
        this.hasFranchiseQueryParam
      )
    },

    isDescriptionDisabled() {
      return (
        this.automaticCampaign ||
        this.includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']) ||
        this.isDisabledInRules(rules.description) ||
        this.hasFranchiseQueryParam
      )
    },

    proposalDeadlineFormatted() {
      if (this.campanha.industry.proposal_deadline) {
        return DateTime.fromISO(this.campanha.industry.proposal_deadline).toFormat('dd/MM/yyyy HH:mm')
      } else {
        return '-'
      }
    },

    getAcceptHistoryUser() {
      const { accept_history } = this.campanha.industry
      if (accept_history && accept_history.user && accept_history.user.name && accept_history.user.login) {
        return `${accept_history.user.name} (${accept_history.user.login})`
      } else {
        return '-'
      }
    },

    getAcceptHistoryDate() {
      const { accept_history } = this.campanha.industry
      if (accept_history && accept_history.date) {
        return DateTime.fromISO(accept_history.date).toFormat('dd/MM/yyyy HH:mm')
      } else {
        return '-'
      }
    },

    isIndustryCampaign() {
      return Boolean(this.campanha.industry)
    },

    showSegmentsWarningRow() {
      if (this.isIndustryCampaign && !this.hasSegments) return false
      else return true
    },

    hasSegments() {
      return this.campanha.segments && this.campanha.segments.length > 0
    },

    cashbackAndPointsModulesActive() {
      const modulesActive = []
      if (this.hasCashbackModule) modulesActive.push('cashback_percentage_offer_sales', 'cashback_absolute_offer_sales')
      if (this.isPoints && !this.isIndustryCampaign) modulesActive.push('points_offer_sales')
      if (this.isNextbuyCashback) modulesActive.push('next_buy_cashback_offer_sales')
      return modulesActive
    },

    offerTables() {
      const tables = [
        {
          component: AbsoluteDiscount,
          offerSalesType: 'offer_sales',
          show: this.offersTypeTabHighLevel === 0 && this.offersTypeTabLowLevel === 0,
          isVisible: true,
          ref: 'productsAbsoluteDiscount',
        },
        {
          component: PercentageDiscount,
          offerSalesType: 'percentage_offer_sales',
          show: this.offersTypeTabHighLevel === 0 && this.offersTypeTabLowLevel === 1,
          isVisible: this.isAdvancedOffer,
          ref: 'productsPercentageDiscount',
        },
        ...(this.isOffersByUnitPermission
          ? [
              {
                component: DynamicPricingPercentageDiscount,
                offerSalesType: 'dynamic_pricing_percentage_offer_sales',
                show: this.offersTypeTabHighLevel === 0 && this.offersTypeTabLowLevel === 2,
                isVisible: this.isAdvancedOffer,
                ref: 'productsDynamicPricingPercentageDiscount',
              },
            ]
          : []),
        {
          component: TakePay,
          offerSalesType: 'takepay_offer_sales',
          show: this.offersTypeTabHighLevel === 0 && this.offersTypeTabLowLevel === (this.isOffersByUnitPermission ? 3 : 2),
          isVisible: this.isAdvancedOffer,
          ref: 'productsTakeAndPayDiscount',
        },
        {
          component: CashbackAndPoints,
          offerSalesType: 'cashback_percentage_offer_sales',
          show: this.offersTypeTabHighLevel === 1 && this.offersTypeTabLowLevel === this.getCashbackOrPointsIndex('cashback_percentage_offer_sales'),
          isVisible: this.hasCashbackModule && !this.isIntelligentDistributionEnabled,
          ref: 'productsCashbackPercentage',
        },
        {
          component: CashbackAndPoints,
          offerSalesType: 'cashback_absolute_offer_sales',
          show: this.offersTypeTabHighLevel === 1 && this.offersTypeTabLowLevel === this.getCashbackOrPointsIndex('cashback_absolute_offer_sales'),
          isVisible: this.hasCashbackModule && !this.isIntelligentDistributionEnabled,
          ref: 'productsCashbackAbsolute',
        },
        {
          component: CashbackAndPoints,
          offerSalesType: 'points_offer_sales',
          show: this.offersTypeTabHighLevel === 1 && this.offersTypeTabLowLevel === this.getCashbackOrPointsIndex('points_offer_sales'),
          isVisible: this.isPoints && !this.isIndustryCampaign && !this.isIntelligentDistributionEnabled,
          ref: 'productsPoints',
        },
        {
          component: CashbackAndPoints,
          offerSalesType: 'next_buy_cashback_offer_sales',
          show: this.offersTypeTabHighLevel === 1 && this.offersTypeTabLowLevel === this.getCashbackOrPointsIndex('next_buy_cashback_offer_sales'),
          isVisible: this.isNextbuyCashback,
          ref: 'productsNextbuyCashback',
        },
      ]
      return tables
    },

    disableSegmentViewMessage() {
      if (!this.campanha.status || this.includesThisStatus('draft')) return 'Não é possível visualizar critérios de campanhas que estão como rascunho'
      return 'Não é possível visualizar critérios desta campanha'
    },

    isFranchisee() {
      if (!this.$getLoggedUser().is_franchisee) return false
      return this.$getLoggedUser().is_franchisee
    },

    allowsViewingFranchiseTradeResults() {
      return this.$permissions.havePagePermission('campanhas/gestao-ofertas/trade/resultados_franquia')
    },

    hasFranchiseQueryParam() {
      const { franchise } = this.$route.query
      if (typeof franchise === 'string') {
        return franchise.toLowerCase() === 'true'
      }
      return Boolean(franchise)
    },

    disableSave() {
      return (
        this.includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']) ||
        this.isDisabledInRules(this.rules.units) ||
        this.hasFranchiseQueryParam
      )
    },

    isDisabled() {
      return this.disableButtons || this.includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']) || this.hasFranchiseQueryParam
    },
  },

  async mounted() {
    if (this.isNewOffer) {
      await this.prebuildAutoOfferSchedule()
      if (!this.hasFlashSales) {
        this.campanha.start_time = '00:00'
        this.campanha.end_time = '23:59'
      }
    } else {
      this.loadingCampaign = true
      await this.getData()
      this.checkUnitsPermissions()
      this.focusOnTableWithData()
      if (this.allDynamicPricingPercentageOfferSalesProducts.length > 0) this.setDynamicPricingPercentageSalesProductsPrice1Fields()

      this.initializeIntelligentDistribution(this.campanha)
    }
  },

  methods: {
    ...mapActions(['unsetQuickAction']),
    cloneDeep,

    clearFlashSalesFields() {
      if (this.campanha.start_time) {
        this.campanha.start_time = '00:00'
      }
      if (this.campanha.end_time) {
        this.campanha.end_time = '23:59'
      }
      if (this.campanha.pdv_margin_time) {
        this.campanha.pdv_margin_time = 0
      }
      if (this.campanha.flash) {
        this.campanha.flash = false
      }
    },
    handleSelectStartDate() {
      if (!this.hasFlashSales) return
      this.campanha.start_time = '00:00'
      this.handleSelectStartTime()
    },
    handleSelectStartTime() {
      if (this.hasFlashSales) {
        this.campanha.end_date = ''
        this.campanha.end_time = ''
      }
    },
    handleSelectEndDate() {
      if (this.hasFlashSales) {
        this.campanha.end_time = '23:59'
      }
    },
    searchOfferProducts(skus) {
      const variables = { skus }

      if (this.isOffersByUnitPermission) {
        variables.units = this.campanha.units
      }

      const allProducts = this.$apollo
        .query({
          query: QUERY_GET_PRODUCTS_AND_GROUPS,
          variables,
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { products: { products } = {} } = {} }) => products)

      return allProducts
    },
    async setDynamicPricingPercentageSalesProductsPrice1Fields() {
      const campaignProducts = this.allDynamicPricingPercentageOfferSalesProducts
      const skus = campaignProducts.map((product) => product.sku)
      const allProducts = await this.searchOfferProducts(skus)
      let productsSkusKeysAndUnitsPrice1 = {}
      allProducts.map((product) => {
        productsSkusKeysAndUnitsPrice1[product.sku] = product?.units_with_price_1
      })

      const productsToInsert = this.campanha.dynamic_pricing_percentage_offer_sales.map((product) => {
        if (product?.isGroup) {
          product.products = product.products.map((groupProduct) => {
            groupProduct.units_with_price_1 = productsSkusKeysAndUnitsPrice1[groupProduct.sku]
            return groupProduct
          })
        } else {
          product.units_with_price_1 = productsSkusKeysAndUnitsPrice1[product.sku]
        }
        return product
      })
      this.insertProducts({ offerSalesKeyName: 'dynamic_pricing_percentage_offer_sales', products: productsToInsert })
    },
    insertProducts({ offerSalesKeyName, products }) {
      this.campanha[offerSalesKeyName] = products
    },
    emitResultsFunction() {
      let route = ''
      if (this.isIndustryCampaign) {
        const routeName = this.campanha.industry
          ? this.campanha.industry.is_mercapromo
            ? 'ofertas-trade-resultado-mercapromo'
            : 'ofertas-trade-resultado'
          : 'ofertas-trade-resultado'
        route = this.$router.resolve({ name: routeName, params: { id: this.campanha._id } })
      } else {
        route = this.$router.resolve({ name: 'gestao-ofertas-resultado', params: { id: this.campanha._id } })
      }
      window.open(route.href, '_blank')
    },
    updateEans(eansProduct, group, offerSalesKeyName = 'offer_sales', propertyName) {
      const { eans, sku } = eansProduct
      if (offerSalesKeyName === 'combo_offer_sales') {
        let productPosition
        let itemPosition
        this.campanha[offerSalesKeyName].forEach((item, index) => {
          const positionIndex = findIndex(item[propertyName], group)

          if (positionIndex >= 0) {
            productPosition = positionIndex
            itemPosition = index
          }
        })

        if (group.isGroup) {
          const indexProduct = this.campanha[offerSalesKeyName][itemPosition][propertyName][productPosition].products.findIndex(
            (product) => product.sku === sku
          )
          this.campanha[offerSalesKeyName][itemPosition][propertyName][productPosition].products[indexProduct].eans = eans
        } else {
          this.campanha[offerSalesKeyName][itemPosition][propertyName][productPosition].eans = eans
        }

        return
      }
      const indexGroup = findIndex(this.campanha[offerSalesKeyName], group)

      if (group.isGroup) {
        const indexProduct = this.campanha[offerSalesKeyName][indexGroup].products.findIndex((product) => product.sku === sku)
        this.campanha[offerSalesKeyName][indexGroup].products[indexProduct].eans = eans
      } else {
        this.campanha[offerSalesKeyName][indexGroup].eans = eans
      }
    },
    querySelections(query) {
      if (!query || query.length < 3) return
      this.loading = true
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.searchItem(query)
      }, 500)
    },

    async validateInfos() {
      this.allInputsRequired = true
      this.tryingToSaveAsDraft = false
      this.$v.$reset()
      this.$v.$touch()

      if (this.allSingleProducts.length > 1500) {
        this.$snackbar({
          message: 'Você atingiu o limite máximo de 1.500 produtos para esta campanha.',
          snackbarColor: 'error',
        })
        return false
      }

      if (this.$v.$error) {
        this.scrollWindowToError('.error--text')
        return
      }

      if (this.isIndustryCampaign) {
        if (!this.$refs.headerTrade.validNameAndUnits()) {
          this.scrollWindowToError('.error--text')
          return false
        }

        if (this.canAlterTradeOffers) {
          if (!this.validTradeOffersDate()) {
            this.scrollWindowToError('.error--text')
            return false
          }
        }
      }

      const allOfferSalesValidations = []
      const offerSalesTabValidations = []

      if (this.allOfferSalesProducts?.length) {
        const isValid = await this.$refs.productsAbsoluteDiscount[0].checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'offer_sales', index: 0 })
      }
      if (this.allPercentageOfferSalesProducts?.length) {
        const isValid = await this.$refs.productsPercentageDiscount[0].checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'percentage_offer_sales', index: 1 })
      }
      if (this.allDynamicPricingPercentageOfferSalesProducts?.length) {
        const isValid = await this.$refs.productsDynamicPricingPercentageDiscount[0].checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'dynamic_pricing_percentage_offer_sales', index: 2 })
      }
      if (this.allTakeAndPayOfferSalesProducts?.length) {
        const isValid = await this.$refs.productsTakeAndPayDiscount[0].checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'takepay_offer_sales', index: 2 })
      }
      if (this.campanha.combo_offer_sales?.length) {
        const isValid = this.$refs.productsCombo.checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'combo_offer_sales', index: 3 })
      }
      if (this.allCashbackPercentageOfferSalesProducts?.length) {
        const isValid = await this.$refs.productsCashbackPercentage[0].checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'cashback_percentage_offer_sales', index: 4 })
      }
      if (this.allCashbackAbsoluteOfferSalesProducts?.length) {
        const isValid = await this.$refs.productsCashbackAbsolute[0].checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'cashback_absolute_offer_sales', index: 5 })
      }
      if (this.allPointsOfferSalesProducts?.length) {
        const isValid = await this.$refs.productsPoints[0].checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'cashback_points_offer_sales', index: 6 })
      }
      if (this.allNextbuyCashbackOfferSalesProducts?.length) {
        const isValid = await this.$refs.productsNextbuyCashback[0].checkValidations()
        allOfferSalesValidations.push(isValid)
        if (!isValid) offerSalesTabValidations.push({ name: 'next_buy_cashback_offer_sales', index: 7 })
      }

      this.allOfferSalesValidations = offerSalesTabValidations
      const allOfferSalesIsValid = allOfferSalesValidations.every((offerSalesIsValid) => offerSalesIsValid)
      if (!allOfferSalesValidations.length) {
        this.$snackbar({ message: 'É necessário adicionar ao menos um produto em uma oferta para ativar ou agendar uma oferta', snackbarColor: 'error' })
        return false
      } else if (!allOfferSalesIsValid) {
        return false
      } else if (this.offersTypeTabsWithErrorsLowLevel.length) {
        this.$snackbar({ message: 'Há produtos repetidos entre ofertas', snackbarColor: 'error' })
        return false
      }

      if (this.isIntelligentDistributionEnabled) {
        const validationResult = this.validateIntelligentDistribution()
        if (!validationResult.isValid) {
          this.$snackbar({
            message: validationResult.message,
            snackbarColor: 'error',
          })
          return false
        }
      }

      if (this.campanha.industry) {
        if (this.campanha.status === 'industry_proposal' && this.proposalDeadlineExpired()) {
          Swal.fire({
            text: 'Não é possível ativar essa campanha pois o prazo de adesão expirou',
            icon: 'warning',
            showConfirmButton: true,
            customClass: {
              content: 'content-class',
            },
          })
          this.scrollWindowToError('#proposal-deadline')
          return
        }

        if (!this.termsConditionsAccepted) {
          Swal.fire({
            text: 'É necessário aceitar os termos de adesão antes de aceitar a campanha',
            icon: 'warning',
            showConfirmButton: true,
            customClass: {
              content: 'content-class',
            },
          })
          this.scrollWindowToError('#terms-conditions')
          return
        }
      }

      this.confirmCampaign = true
      window.scrollTo(0, 0)
    },

    formatAndValidMargin() {
      this.campanha.pdv_margin_time = this.campanha.pdv_margin_time || 0
    },

    async saveActive() {
      this.loading = true
      this.unavailableProducts = []
      try {
        if (this.hasFlashSales) {
          this.formatAndValidMargin()
        }
        await this.saveCampaign()
      } catch (e) {
        console.log(e)
        this.$snackbar({ message: 'Ocorreu um erro ao salvar campanha.' })
      } finally {
        this.loading = false
      }
    },

    saveCampaign() {
      this.validateInfos()
    },

    async getData() {
      try {
        if (this.$route.params.id) {
          this.loading = true
          await this.$apollo
            .query({
              query: QUERY_SALES_CAMPAIGN_BY_ID,
              variables: {
                id: this.$route.params.id,
                franchise: this.hasFranchiseQueryParam,
              },
              context: {
                uri: this.$microservicesUrls['sales'],
              },
              fetchPolicy: 'no-cache',
            })
            .then((response) => {
              this.loading = false
              const { offerSchedule } = response.data
              const formattedCampaign = this.formatCampaign(offerSchedule)
              this.campanha = {
                ...cloneDeep(formattedCampaign),
              }

              if (this.campanha.segments.length > 0) {
                if (this.campanha.segments[0].fixed) {
                  this.isStaticSegment = true
                }
              }
              if (this.campanha.units_configuration.length) this.defaultDataType = this.campanha.units_configuration[0].dataType
              this.automaticCampaign = Boolean(['erp'].includes(this.campanha?.origin))
              this.termsConditionsAccepted = this.includesThisStatus(['active', 'finished', 'scheduled', 'processing'])
              this.loadingCampaign = false
              if (this.campanha?.industry?.is_mercapromo) {
                this.backToOffersList(true)
              }
            })

          if (this.campanha.cashback_percentage_offer_sales.length > 0 && this.campanha.cashback_percentage_offer_sales?.[0]?.expiration_cashback) {
            this.cashbackExpiration = this.campanha.cashback_percentage_offer_sales?.[0]?.expiration_cashback
          } else if (this.campanha.cashback_absolute_offer_sales.length > 0 && this.campanha.cashback_absolute_offer_sales?.[0]?.expiration_cashback) {
            this.cashbackExpiration = this.campanha.cashback_absolute_offer_sales?.[0]?.expiration_cashback
          }

          delete this.cashbackExpiration.__typename
        }
      } catch (error) {
        console.error(error)
        this.$snackbar({ message: 'Erro ao buscar dados no servidor', snackbarColor: 'error' })
        this.loadingCampaign = false
      }
    },

    async saveDraft() {
      this.loading = true
      try {
        if (this.isIndustryCampaign) {
          if (!this.$refs.headerTrade.validNameAndUnits()) {
            this.scrollWindowToError('.error--text')
            return false
          }

          if (this.canAlterTradeOffers) {
            if (!this.validTradeOffersDate()) {
              this.scrollWindowToError('.error--text')
              return false
            }
          }
        }

        await this.save(true)
      } catch (e) {
        this.$snackbar({ message: 'Ocorreu um erro ao salvar campanha.' })
      } finally {
        this.loading = false
      }
    },

    async checkForIndustryBeforeSave() {
      if (this.campanha.industry && this.campanha.status === 'industry_proposal') {
        const result = await Swal.fire({
          title: 'Confirmar campanha trade',
          html: `
            <div style="text-align: left; font-size: 16px; margin-top: 8px;">
              <span>Para garantir uma melhor experiência para você e seus clientes, sugerimos:</span>
            </div>
            <div style="text-align: left; margin: 16px; font-size: 15px; margin-left: 40px;">
              <ul style="margin-top: 16px">
                <li>Verificar como está o estoque dos produtos oferecidos nesta campanha</li>
                <li style="margin-top: 8px">Informar o time dentro da loja sobre as mecânicas e datas da ação</li>
                <li style="margin-top: 8px">Reforçar comunicação das ofertas nos mais diversos canais (Enxoval de Loja, Mídias Sociais, Whatsapp, SMS, Push etc), aumentará o alcance da campanha</li>
              </ul>
            </div>
          `,
          ...this.industrySwalOptions,
        })

        if (result.value) {
          this.save(false)
        }
      } else {
        this.save(false)
      }
    },

    async save(draft) {
      try {
        if (this.$refs.confirm) this.$refs.confirm.loading = true
        this.allInputsRequired = false
        this.tryingToSaveAsDraft = draft
        this.$v.$reset()
        this.$v.$touch()

        if ((this.$v.$error || this.$v.$invalid) && !this.$v.campanha.start_date.isFuture && this.campanha.status !== 'active') {
          this.scrollWindowToError('.error--text')
          return
        } else {
          this.$v.$reset()
        }

        if (this.isIndustryCampaign) {
          let confirmed = true
          if (this.proposalDeadlineExpired()) {
            confirmed = await this.$confirm({
              ...this.industryAlterationWarning(),
            })
              .then(() => true)
              .catch(() => false)
          }
          if (!confirmed) return
        }

        let segments_ids = []
        if (this.campanha.segments.length > 0) {
          if (!draft && !this.campanha.segments[0].fixed) {
            const variables = {
              segments_ids: [this.campanha.segments[0]._id],
              name: this.campanha.segments[0].name,
              description: this.campanha.segments[0].description,
            }

            if (!this.campanha.industry) {
              const {
                data: { staticallyCloneSegments },
              } = await this.$apollo.mutate({
                mutation: MUTATION_STATICALLY_CLONE_SEGMENTS,
                variables,
                fetchPolicy: 'no-cache',
              })
              staticallyCloneSegments.fixed = true
              this.campanha.segments = [staticallyCloneSegments]

              variables['segments_ids'].map((segmentId) => {
                segments_ids.push({
                  dynamic_id: segmentId,
                  static_id: this.campanha.segments[0]._id,
                })
              })
              this.segmentsIds = cloneDeep(segments_ids)
            }
          }
        }

        if (this.cashbackExpiration.type === 'AT_PURCHASE') {
          this.cashbackExpiration.expires_at = null
        } else {
          this.cashbackExpiration.expires_at = this.getFormatedCashbackExpirationDate(this.cashbackExpiration?.expires_at)
          this.cashbackExpiration.expiration_in_days = null
        }

        const campaignToSave = this.formatCampaignToSave(draft)
        const mutation = draft ? MUTATION_UPSERT_DRAFT_SALES_CAMPAIGN : MUTATION_UPSERT_SALES_CAMPAIGN
        if (!draft) {
          const { process_time_estimation, _id, error_metadata, products_availability_erp_offers } = await this.finishSaveCampaign(campaignToSave, mutation)
          if (this.$getAllowedModules()['check_erp_offers'] && error_metadata?.error_code === 'ERR_PRODUCTS_ALREADY_IN_ERP_OFFERS') {
            this.unavailableProducts = products_availability_erp_offers.unavailable_products
            this.$refs.confirm.loading = false
          } else {
            this.newOfferScheduleIdToCompare = _id
            this.timerTotalValue = process_time_estimation
          }
        } else {
          const offerScheduleId = await this.finishSaveDraftCampaign(campaignToSave, mutation)
          await this.finishSubscription(offerScheduleId, draft)
        }
      } catch (error) {
        this.loading = false
        Swal.fire({
          text: 'Erro ao salvar campanha',
          icon: 'error',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })
      }
    },
    async finishSaveCampaign(campaignToSave, mutation) {
      const {
        data: { upsertOfferSchedule },
      } = await this.$apollo.mutate({
        mutation,
        variables: {
          data: campaignToSave,
        },
        context: {
          uri: this.$microservicesUrls['sales'],
        },
      })
      return upsertOfferSchedule
    },
    async finishSaveDraftCampaign(campaignToSave, mutation) {
      const {
        data: { upsertDraftOfferSchedule },
      } = await this.$apollo.mutate({
        mutation,
        variables: {
          data: campaignToSave,
        },
        context: {
          uri: this.$microservicesUrls['sales'],
        },
      })
      return upsertDraftOfferSchedule
    },
    handleSubscriptionError() {
      this.loading = false
      this.showErrorDialog = true
    },
    async finishSubscription(offerScheduleId, draft = false) {
      if (this.campanha.segments.length > 0 && this.segmentsIds.length > 0 && !this.campanha.industry) {
        if (!draft && !this.campanha.has_segment_data) {
          await this.$apollo.mutate({
            mutation: MUTATION_SAVE_CRITERIA_ON_OFFER_SCHEDULE,
            variables: {
              input: {
                offer_schedule_id: offerScheduleId,
                segments_ids: this.segmentsIds,
              },
            },
            context: {
              uri: this.$microservicesUrls['sales'],
            },
            fetchPolicy: 'no-cache',
          })
        }
      }

      if (
        (!this.campanha._id && !draft) ||
        (this.campanha._id && (this.campanha.status === 'draft' || this.campanha.status === 'industry_proposal') && !draft)
      ) {
        this.offerScheduleId = offerScheduleId
        this.confirmCampaign = false
        this.campaignActivated = true
      } else {
        Swal.fire({
          text: 'Campanha salva',
          icon: 'success',
          showConfirmButton: false,
          timer: 1000,
          onClose: () => {
            this.confirmCampaign = false
            this.backToOffersList()
          },
          customClass: {
            content: 'content-class',
          },
        })
      }
    },
    backToOffersList(skipRouteValidaton = false) {
      if (skipRouteValidaton) this.skipRouteValidaton = true
      let routeName = 'gestao-ofertas'
      if (this.campanha.industry) {
        routeName = 'ofertas-trade-propostas'
      }
      this.$router.push({ name: routeName }).catch(() => {})
    },

    includesThisStatus(status) {
      return status.includes(this.campanha.status)
    },

    async prebuildAutoOfferSchedule() {
      if (!this.getIsQuickAction() || !this.quickAction?.segmentation) {
        return
      }

      this.campanha.segments.push(this.quickAction.segmentation.segment)
      this.campanha.name = this.quickAction.segmentation.title
      this.campanha.description = this.quickAction.segmentation.description

      this.unsetQuickAction()
    },

    checkUnitsPermissions() {
      if (!this.$getIsAdmin() && this.campanha?.units.length && this.campanha?.units?.some((unit) => !this.units.find((u) => u.id_erp === unit))) {
        this.disableButtons = true
        this.disabledUnitsPermission = true
      }
    },

    isDisabledInRules(fieldName) {
      if (!this.campanha?.industry || !this.campanha?.rules || this.canAlterTradeOffers) return false
      const rule = this.campanha?.rules.find((rule) => rule.key === fieldName)
      if (!rule) return false
      return !rule.editable
    },

    async confirmBeforeFinish() {
      let confirmed = false

      if (this.campanha.industry) {
        confirmed = await this.$confirm({
          ...this.industryFinishWarning('concluída'),
        })
          .then(() => true)
          .catch(() => false)
      } else {
        confirmed = await this.$confirm({
          title: 'Finalizar campanha',
          message: 'A campanha será movida para o status concluída',
          confirmText: 'Ok',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (confirmed) {
        await this.finishCampaign()
      }
    },

    async finishCampaign() {
      this.loading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_FINISH_SALES_CAMPAIGN,
          variables: {
            id: this.campanha._id,
          },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
        })
        .then(() => {
          this.backToOffersList()
        })
        .catch((error) => {
          console.log(error)
          this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
        })
      this.loading = false
    },

    async confirmBeforeDisable() {
      let confirmed = false

      if (this.campanha.industry && this.proposalDeadlineExpired()) {
        confirmed = await this.$confirm({
          ...this.industryFinishWarning('expirada'),
        })
          .then(() => true)
          .catch(() => false)
      } else {
        const statusMsg = this.campanha.industry ? 'proposta indústria' : 'rascunho'
        confirmed = await this.$confirm({
          message: `A campanha será movida para o status ${statusMsg}`,
          confirmText: 'Ok',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (confirmed) {
        await this.disableDraft()
      }
    },

    async disableDraft() {
      this.loading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_UNSCHEDULED_SALES_CAMPAIGN,
          variables: {
            id: this.campanha._id,
          },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
        })
        .then(() => {
          this.backToOffersList()
        })
        .catch((error) => {
          console.log(error)
          this.$snackbar(this.$t('campanhas.snackbars.errors.delete'))
        })
      this.loading = true
    },

    async deleteCampaign() {
      const confirmed = await this.$confirm({
        title: 'Remover campanha',
        message: 'Tem certeza que deseja remover esta campanha?',
        confirmText: 'Remover',
        confirmColor: 'vermelho-d20',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return

      this.loading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_DELETE_SALES_CAMPAIGN,
          variables: {
            id: this.campanha._id,
          },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
        })
        .then(() => {
          this.backToOffersList()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.delete'))
        })
      this.loading = false
    },

    proposalDeadlineExpired() {
      return DateTime.now() > DateTime.fromISO(this.campanha.industry.proposal_deadline).endOf('day')
    },

    scrollWindowToError(querySelector) {
      this.$nextTick(() => {
        let element = document.querySelector(querySelector)
        if (element) {
          this.$vuetify.goTo(element, { offset: 200 })
        } else {
          this.$snackbar({ message: 'Erro ao ativar campanha, por favor, verifique os campos', snackbarColor: 'error' })
          window.scrollTo(0, 0)
        }
      })
    },

    validateIntelligentDistribution() {
      if (!this.campanha.segments || this.campanha.segments.length === 0) {
        return {
          isValid: false,
          message: 'Para usar a distribuição inteligente, selecione ao menos um segmento.',
        }
      }

      const totalOffers = this.getTotalAvailableOffers()

      if (totalOffers === 0) {
        return {
          isValid: false,
          message: 'Adicione ao menos uma oferta para configurar a distribuição inteligente.',
        }
      }

      const offersPerConsumer = this.offersPerConsumer
      if (!offersPerConsumer || offersPerConsumer < 1 || !Number.isInteger(offersPerConsumer)) {
        return {
          isValid: false,
          message: 'Defina a quantidade máxima de ofertas por cliente (mínimo 1).',
        }
      }

      if (offersPerConsumer > totalOffers && this.campanha.status !== 'active') {
        return {
          isValid: false,
          message: 'A quantidade de ofertas por cliente não pode ser maior que o total de ofertas disponíveis.',
        }
      }

      const distributionValidation = this.calculateDistributionValidation()

      if (!distributionValidation.isValid) {
        return {
          isValid: false,
          message: distributionValidation.message,
        }
      }

      return { isValid: true }
    },

    getTotalConsumersCount() {
      if (!this.campanha.segments || this.campanha.segments.length === 0) {
        return 0
      }

      return this.campanha.segments.reduce((total, segment) => {
        return total + (segment.clients_qty || 0)
      }, 0)
    },

    getTotalAvailableOffers() {
      let total = 0

      if (this.campanha) {
        const offerTypes = [
          'offer_sales',
          'percentage_offer_sales',
          'dynamic_pricing_percentage_offer_sales',
          'takepay_offer_sales',
          'combo_offer_sales',
          'cashback_percentage_offer_sales',
          'cashback_absolute_offer_sales',
          'points_offer_sales',
          'next_buy_cashback_offer_sales',
          'dynamic_rule_offer_sales',
          'cross_sell_offer_sales',
        ]

        offerTypes.forEach((type) => {
          if (this.campanha[type] && Array.isArray(this.campanha[type])) {
            this.campanha[type].forEach((offer) => {
              if (offer.isGroup && offer.products && Array.isArray(offer.products)) {
                if (offer.group_rules_exhibition === 'PUBLIC') {
                  const hasActiveProducts = offer.products.some((product) => product.active)
                  if (hasActiveProducts) {
                    total += 1
                  }
                } else {
                  offer.products.forEach((product) => {
                    if (product.active) {
                      total += 1
                    }
                  })
                }
              } else if (offer.active) {
                total += 1
              }
            })
          }
        })
      }

      return total
    },

    getTotalPossibleDistributions() {
      let totalDistributions = 0

      if (this.campanha) {
        const offerTypes = [
          'offer_sales',
          'percentage_offer_sales',
          'dynamic_pricing_percentage_offer_sales',
          'takepay_offer_sales',
          'combo_offer_sales',
          'cashback_percentage_offer_sales',
          'cashback_absolute_offer_sales',
          'points_offer_sales',
          'next_buy_cashback_offer_sales',
          'dynamic_rule_offer_sales',
          'cross_sell_offer_sales',
        ]

        offerTypes.forEach((type) => {
          if (this.campanha[type] && Array.isArray(this.campanha[type])) {
            this.campanha[type].forEach((offer) => {
              if (offer.isGroup && offer.products && Array.isArray(offer.products)) {
                offer.products.forEach((product) => {
                  if (product.active) {
                    const distributionLimit = product.distribution_limit
                    if (distributionLimit && distributionLimit > 0) {
                      totalDistributions += distributionLimit
                    } else {
                      totalDistributions += 999999
                    }
                  }
                })
              } else if (offer.active) {
                const distributionLimit = offer.distribution_limit
                if (distributionLimit && distributionLimit > 0) {
                  totalDistributions += distributionLimit
                } else {
                  totalDistributions += 999999
                }
              }
            })
          }
        })
      }

      return totalDistributions
    },

    calculateDistributionValidation() {
      if (this.campanha.status === 'active') {
        return {
          isValid: true,
          message: '',
          calculation: {
            totalConsumers: 0,
            offersPerConsumer: 0,
            totalNeeded: 0,
            unlimitedProducts: 0,
            limitedProductsSum: 0,
            finalResult: 0,
          },
        }
      }

      const totalConsumers = this.getTotalConsumersCount()
      const offersPerConsumer = this.offersPerConsumer

      if (totalConsumers === 0 || !offersPerConsumer) {
        return {
          isValid: true,
          message: '',
          calculation: {
            totalConsumers: 0,
            offersPerConsumer: 0,
            totalNeeded: 0,
            unlimitedProducts: 0,
            limitedProductsSum: 0,
            finalResult: 0,
          },
        }
      }

      const totalNeeded = totalConsumers * offersPerConsumer

      let unlimitedProductsCount = 0
      let limitedProductsSum = 0
      let productsWithFullSegmentLimit = 0
      const insufficientProducts = []

      const offerTypes = [
        'offer_sales',
        'percentage_offer_sales',
        'dynamic_pricing_percentage_offer_sales',
        'takepay_offer_sales',
        'combo_offer_sales',
        'cashback_percentage_offer_sales',
        'cashback_absolute_offer_sales',
        'points_offer_sales',
        'next_buy_cashback_offer_sales',
        'dynamic_rule_offer_sales',
        'cross_sell_offer_sales',
      ]

      offerTypes.forEach((type) => {
        if (this.campanha[type] && Array.isArray(this.campanha[type])) {
          this.campanha[type].forEach((offer) => {
            if (offer.isGroup && offer.products && Array.isArray(offer.products)) {
              const hasActiveProducts = offer.products.some((product) => product.active)

              if (hasActiveProducts) {
                if (offer.group_rules_exhibition === 'PUBLIC') {
                  const distributionLimit = offer.distribution_limit
                  if (!distributionLimit || distributionLimit <= 0) {
                    unlimitedProductsCount += 1
                    productsWithFullSegmentLimit += 1
                  } else {
                    limitedProductsSum += distributionLimit
                    if (distributionLimit >= totalConsumers) {
                      productsWithFullSegmentLimit += 1
                    }

                    if (distributionLimit < totalConsumers) {
                      insufficientProducts.push({
                        name: offer.name || 'Grupo sem nome',
                        limit: distributionLimit,
                        required: totalConsumers,
                      })
                    }
                  }
                } else {
                  offer.products.forEach((product) => {
                    if (product.active) {
                      const distributionLimit = product.distribution_limit
                      if (!distributionLimit || distributionLimit <= 0) {
                        unlimitedProductsCount += 1
                        productsWithFullSegmentLimit += 1
                      } else {
                        limitedProductsSum += distributionLimit
                        if (distributionLimit >= totalConsumers) {
                          productsWithFullSegmentLimit += 1
                        }

                        if (distributionLimit < totalConsumers) {
                          insufficientProducts.push({
                            name: product.name || product.sku || 'Produto sem nome',
                            limit: distributionLimit,
                            required: totalConsumers,
                          })
                        }
                      }
                    }
                  })
                }
              }
            } else if (offer.active) {
              const distributionLimit = offer.distribution_limit
              if (!distributionLimit || distributionLimit <= 0) {
                unlimitedProductsCount += 1
                productsWithFullSegmentLimit += 1
              } else {
                limitedProductsSum += distributionLimit
                if (distributionLimit >= totalConsumers) {
                  productsWithFullSegmentLimit += 1
                }

                if (distributionLimit < totalConsumers) {
                  insufficientProducts.push({
                    name: offer.name || offer.sku || 'Produto sem nome',
                    limit: distributionLimit,
                    required: totalConsumers,
                  })
                }
              }
            }
          })
        }
      })

      const unlimitedProductsDeduction = unlimitedProductsCount * totalConsumers
      const totalAvailable = unlimitedProductsDeduction + limitedProductsSum

      if (productsWithFullSegmentLimit < offersPerConsumer) {
        return {
          isValid: false,
          message: `Pelo menos ${offersPerConsumer} produto(s) devem ter limite de ofertas de pelo menos ${totalConsumers} ofertas (ou ilimitado) para atender todos os clientes do segmento.`,
          calculation: {
            totalConsumers,
            offersPerConsumer,
            totalNeeded,
            unlimitedProducts: unlimitedProductsCount,
            limitedProductsSum,
            totalAvailable,
            insufficientProducts,
            finalResult: 0,
          },
        }
      }

      if (totalAvailable < totalNeeded) {
        return {
          isValid: false,
          message: `A campanha não pode ser ativada. Há menos produtos cadastrados do que o número de ofertas distribuidas por cliente. Cadastre mais produtos ou reduza as ofertas por cliente.`,
          calculation: {
            totalConsumers,
            offersPerConsumer,
            totalNeeded,
            unlimitedProducts: unlimitedProductsCount,
            limitedProductsSum,
            totalAvailable,
            insufficientProducts,
            finalResult: totalNeeded - totalAvailable,
          },
        }
      }

      return {
        isValid: true,
        message: '',
        calculation: {
          totalConsumers,
          offersPerConsumer,
          totalNeeded,
          unlimitedProducts: unlimitedProductsCount,
          unlimitedProductsDeduction,
          limitedProductsSum,
          totalAvailable,
          finalResult: 0,
        },
      }
    },

    getCashbackOrPointsIndex(offerType) {
      return this.cashbackAndPointsModulesActive.findIndex((offerSaleType) => offerSaleType === offerType)
    },

    focusOnTableWithData() {
      if (this.campanha?.offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 0
        this.offersTypeTabLowLevel = 0
      } else if (this.campanha?.percentage_offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 0
        this.offersTypeTabLowLevel = 1
      } else if (this.campanha?.dynamic_pricing_percentage_offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 0
        this.offersTypeTabLowLevel = 2
      } else if (this.campanha?.takepay_offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 0
        this.offersTypeTabLowLevel = this.isOffersByUnitPermission ? 3 : 2
      } else if (this.campanha?.combo_offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 0
        this.offersTypeTabLowLevel = this.isOffersByUnitPermission ? 4 : 3
      } else if (this.campanha?.cashback_percentage_offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 1
        this.offersTypeTabLowLevel = this.getCashbackOrPointsIndex('cashback_percentage_offer_sales')
      } else if (this.campanha?.cashback_absolute_offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 1
        this.offersTypeTabLowLevel = this.getCashbackOrPointsIndex('cashback_absolute_offer_sales')
      } else if (this.campanha?.points_offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 1
        this.offersTypeTabLowLevel = this.getCashbackOrPointsIndex('points_offer_sales')
      } else if (this.campanha?.next_buy_cashback_offer_sales?.length > 0) {
        this.offersTypeTabHighLevel = 1
        this.offersTypeTabLowLevel = this.getCashbackOrPointsIndex('next_buy_cashback_offer_sales')
      }
    },

    updateAcceptance(accepted) {
      this.termsConditionsAccepted = accepted
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.skipRouteValidaton) {
      next()
      return
    }
    if (this.confirmCampaign) {
      this.$confirm(this.getConfirmMessage)
        .then(() => {
          next()
        })
        .catch(() => {
          next(false)
        })
    } else {
      next()
    }
  },
  validations() {
    let expirationCashbackValidation = {}
    if (this.campanha.cashback_percentage_offer_sales.length > 0 || this.campanha.cashback_absolute_offer_sales.length > 0) {
      expirationCashbackValidation = { cashbackExpiration: { type: { required } } }
      if (this.cashbackExpiration.type === 'AT_PURCHASE') {
        expirationCashbackValidation.cashbackExpiration.expiration_in_days = {
          minValue: minValue(1),
          required,
        }
      } else {
        expirationCashbackValidation.cashbackExpiration.expires_at = {
          required,
          endDateValidation: (value) => {
            if (DateTime.fromISO(value) <= DateTime.fromISO(this.campanha.end_date)) return false
            return true
          },
        }
      }
    }

    if (this.tryingToSaveAsDraft) {
      return {
        campanha: {
          name: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
        },
      }
    } else if (this.campanha.status === 'active') {
      return {
        ...expirationCashbackValidation,
        campanha: {
          name: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
          description: {
            required,
          },
          start_date: {
            required,
          },
          end_date: {
            required,
            endDateBiggerThanNow: (value) => {
              if (DateTime.fromISO(value) < DateTime.fromISO(DateTime.now().toFormat('yyyy-MM-dd'))) return false
              return true
            },
          },
          pdv_margin_time: {
            ...(this.hasFlashSales && !this.isIntelligentDistributionEnabled
              ? {
                  validateMargin: (value) => {
                    if (value < 0 || value > 60) return false
                    return true
                  },
                }
              : {}),
          },
          units: {
            required,
          },
        },
      }
    } else {
      return {
        ...expirationCashbackValidation,
        campanha: {
          name: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
          description: {
            required,
          },
          start_time: {
            ...(this.hasFlashSales && !this.isIntelligentDistributionEnabled ? { required } : {}),
          },
          end_time: {
            ...(this.hasFlashSales && !this.isIntelligentDistributionEnabled ? { required } : {}),
          },
          start_date: {
            required,
            ...(this.isIndustryCampaign ? {} : { isFuture }),
          },
          end_date: {
            required,
            endDateBiggerThanStart: (value) => {
              if (DateTime.fromISO(value) < DateTime.fromISO(this.campanha.start_date)) return false
              return true
            },
            ...(this.isIndustryCampaign ? {} : { isFuture }),
          },
          pdv_margin_time: {
            ...(this.hasFlashSales && !this.isIntelligentDistributionEnabled
              ? {
                  validateMargin: (value) => {
                    if (value < 0 || value > 60) return false
                    return true
                  },
                }
              : {}),
          },
          units: {
            required,
          },
        },
      }
    }
  },
}
</script>

<style lang="scss">
.alteracao-gesta-ofertas {
  .titulo_card {
    font-size: 24px;
    font-weight: bold;
    color: $cinza-d20;
  }

  .sub-title {
    font-size: 1.05rem;
  }
}

.terms-conditions-text {
  cursor: default;
  user-select: none;
}

.margin-left-btn {
  margin-left: 12px;
}

.primary-color {
  background-color: $marca-0;
}

.offer-results-button {
  margin: 5px 0 0 10px;
  .v-btn__content {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0.01em;
    text-align: center;
  }
}

.validity-period {
  display: flex;
  align-items: flex-start;
  gap: 20px;

  .input-container {
    max-width: 240px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    span {
      color: $cinza-d10;
      font-size: 14px;
      font-weight: 600;
    }
  }

  .margin-plus {
    width: -webkit-fill-available;

    .margin-plus-input {
      width: 132px;
      font-weight: 400;

      .v-messages__message {
        width: 130px;
      }
    }
  }

  .separator {
    height: 70px;
    width: 1px;
    background-color: $cinza-0;
  }
}

.flash-sale-container {
  display: flex;
  align-items: flex-start;
  gap: 40px;
  padding-top: 32px;
  border-top: 1px solid $cinza-l70;
  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    margin: 8px 0 0;
    max-width: 285px;
    color: $cinza-0;
  }

  span {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    color: $cinza-d10;
    display: block;
  }
}

.campaign-target-and-model {
  display: flex;
  gap: 2%;
  flex-wrap: wrap;
  margin: 24px 0;
  .card-container {
    min-height: 260px;
    width: 49%;
  }
}

.campaign-model-application {
  margin: 24px 0;
}
.title-description-additional-content {
  .v-divider {
    margin: 16px 0;
  }
  .select-units-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 236px;
    .mf-select-units-label {
      color: $cinza-d10;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
    }
  }
}
</style>
